/**
 * Create event — title, category chips, date, time, venue, description.
 * Wired: POST /api/events. Port of CreateEvent in
 * design/source/screens-poll-event.jsx.
 */
import React, { useState } from 'react';
import { View } from 'react-native';

import { space } from '@bero/tokens';
import { Icon } from '@/components/gate/icons';
import { AddPhotoBox, BottomBar, goBack } from '@/features/community/bits';
import { createEvent, eventCatLabel } from '@/features/community/lib';
import { useT } from '@/i18n';
import { useTheme } from '@/theme';
import { AppText, Button, Chip, Field, IconButton, Input, Screen, TopBar } from '@/ui';

// Submit values stay English (the API expects them); only the labels localise.
const CATEGORIES = ['Festival', 'Sports', 'Kids', 'Cultural', 'Meeting', 'General'];

export default function CreateEventScreen() {
  const { colors } = useTheme();
  const { t } = useT();
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('Festival');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [venue, setVenue] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSave = title.trim().length > 0;

  const submit = async () => {
    if (!canSave || saving) return;
    setSaving(true);
    setError(null);
    try {
      await createEvent({
        title: title.trim(),
        category,
        date: date.trim(),
        time: time.trim(),
        venue: venue.trim(),
        description: description.trim(),
      });
      goBack();
    } catch {
      setError(t('ec_submit_error'));
      setSaving(false);
    }
  };

  return (
    <Screen scroll={false}>
      <TopBar
        title={t('ec_title')}
        onBack={goBack}
        right={
          <IconButton label={t('ec_discard')} onPress={goBack}>
            <Icon name="x" size={20} color={colors.textPrimary} />
          </IconButton>
        }
      />
      <Screen padded style={{ paddingTop: space.s4, gap: space.s4 }}>
        <AddPhotoBox label={t('ec_add_cover')} h={120} />

        <Field label={t('ec_title_label')}>
          <Input value={title} onChangeText={setTitle} placeholder={t('ec_title_ph')} />
        </Field>

        <Field label={t('hd_category')}>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: space.s2 }}>
            {CATEGORIES.map((c) => (
              <Chip key={c} label={eventCatLabel(c, t)} active={category === c} onPress={() => setCategory(c)} />
            ))}
          </View>
        </Field>

        <View style={{ flexDirection: 'row', gap: space.s3 }}>
          <View style={{ flex: 1 }}>
            <Field label={t('ec_date')}>
              <Input value={date} onChangeText={setDate} placeholder={t('ec_date_ph')} />
            </Field>
          </View>
          <View style={{ flex: 1 }}>
            <Field label={t('ec_time')}>
              <Input value={time} onChangeText={setTime} placeholder={t('ec_time_ph')} />
            </Field>
          </View>
        </View>

        <Field label={t('ec_venue')}>
          <Input value={venue} onChangeText={setVenue} placeholder={t('ec_venue_ph')} />
        </Field>

        <Field label={t('hd_description')}>
          <Input
            value={description}
            onChangeText={setDescription}
            placeholder={t('ec_desc_ph')}
            multiline
            style={{ minHeight: 96, textAlignVertical: 'top' }}
          />
        </Field>

        {error ? <AppText variant="bodySm" tone="danger">{error}</AppText> : null}
      </Screen>
      <BottomBar>
        <Button title={t('ec_submit')} size="lg" disabled={!canSave} loading={saving} onPress={() => void submit()} />
      </BottomBar>
    </Screen>
  );
}
